import React, { useState } from 'react';
import { Briefcase, Sparkles, Save, ArrowLeft, Loader2, Mail, MessageSquare, Globe, Flame, BadgeAlert, CheckCircle2 } from 'lucide-react';
import { generateAIContent, getPrompts } from '../aiService';
import { saveJob } from '../jobStorage';
import UpgradeModal from './UpgradeModal';
import { useSubscription } from '../contexts/SubscriptionContext';
import BackgroundLayout from './ui/BackgroundLayout';
import { useTheme } from '../contexts/ThemeContext';

const JobAnalyzer = ({ resumeData, apiConfig, openAIModal, onBack, onSaveSuccess, onTailor, initialJob = null }) => {
    const { isDarkMode } = useTheme();
    // const { unlockedFeatures, purchaseFeature, isPremium } = useSubscription(); // Old linkage
    // We update this in the component body, but ensuring imports are clean.
    const { purchaseFeature, hasFeature, getFeaturePrice } = useSubscription();
    const [jobDescription, setJobDescription] = useState(initialJob?.description || '');
    const [jobTitle, setJobTitle] = useState(initialJob?.title || '');
    const [company, setCompany] = useState(initialJob?.company || '');
    const [jobLink, setJobLink] = useState(initialJob?.link || '');
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(initialJob?.analysis || null);
    const [selectedLanguage, setSelectedLanguage] = useState('English');

    const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian', 'Dutch', 'Chinese', 'Japanese', 'Hindi', 'Arabic', 'Finnish', 'Vietnamese'];

    // Restore other AI states if available
    const [coverLetter, setCoverLetter] = useState(initialJob?.coverLetter || null);
    const [generatingLetter, setGeneratingLetter] = useState(false);

    const [interviewPrep, setInterviewPrep] = useState(initialJob?.interviewPrep || null);
    const [generatingPrep, setGeneratingPrep] = useState(false);

    const [roast, setRoast] = useState(initialJob?.roast || null);
    const [roasting, setRoasting] = useState(false);

    const [showUpgrade, setShowUpgrade] = useState(false);

    const hasApiKey = apiConfig && (apiConfig.gemini || apiConfig.openai || apiConfig.anthropic);

    const handleAnalyze = async () => {
        if (!hasApiKey) {
            openAIModal();
            return;
        }

        if (!jobDescription.trim()) {
            alert('Please paste a job description');
            return;
        }

        setAnalyzing(true);
        try {
            const prompt = getPrompts('job-match', { resume: resumeData, jobDescription }, selectedLanguage);
            const response = await generateAIContent(apiConfig, prompt);

            // Parse JSON response
            const jsonMatch = response.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                const result = JSON.parse(jsonMatch[0]);
                setAnalysis(result);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            alert('Analysis failed: ' + error.message);
        } finally {
            setAnalyzing(false);
        }
    };

    // const { hasFeature, purchaseFeature } = useSubscription(); // Merged above

    const handleGenerateCoverLetter = async () => {
        // Differential Pricing: Check if unlocked
        if (!hasFeature('cover_letter')) {
            purchaseFeature('cover_letter');
            return;
        }

        if (!hasApiKey || !analysis) return;

        setGeneratingLetter(true);
        try {
            const prompt = getPrompts('cover-letter', { resume: resumeData, jobDescription }, selectedLanguage);
            const response = await generateAIContent(apiConfig, prompt);

            const jsonMatch = response.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                setCoverLetter(JSON.parse(jsonMatch[0]));
            }
        } catch (error) {
            alert('Failed to generate cover letter: ' + error.message);
        } finally {
            setGeneratingLetter(false);
        }
    };

    const handleGenerateInterviewPrep = async () => {
        if (!hasApiKey || !analysis) return;

        setGeneratingPrep(true);
        try {
            const prompt = getPrompts('interview-prep', { resume: resumeData, jobDescription }, selectedLanguage);
            const response = await generateAIContent(apiConfig, prompt);

            const jsonMatch = response.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                setInterviewPrep(JSON.parse(jsonMatch[0]));
            }
        } catch (error) {
            alert('Failed to generate interview prep: ' + error.message);
        } finally {
            setGeneratingPrep(false);
        }
    };

    const handleRoastResume = async () => {
        // Differential Pricing: Check if unlocked
        if (!hasFeature('roast')) {
            purchaseFeature('roast');
            return;
        }

        if (!hasApiKey || !jobDescription) {
            alert("Need a job description to roast properly!");
            return;
        }

        setRoasting(true);
        setRoast(null); // Clear previous roast

        try {
            const prompt = getPrompts('roast-resume', { resume: resumeData, jobDescription }, selectedLanguage);
            const response = await generateAIContent(apiConfig, prompt);

            const jsonMatch = response.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                setRoast(JSON.parse(jsonMatch[0]));
            } else {
                throw new Error("Could not parse AI response");
            }
        } catch (error) {
            alert('Roast failed (maybe it was too hot?): ' + error.message);
        } finally {
            setRoasting(false);
        }
    };

    const handleSave = () => {
        if (!analysis) return;

        const job = {
            title: jobTitle || 'Untitled Job',
            company: company || 'Unknown Company',
            link: jobLink,
            description: jobDescription,
            matchScore: analysis.matchScore,
            analysis: analysis,
            status: 'Interested',
            coverLetter: coverLetter,
            interviewPrep: interviewPrep,
            roast: roast,
            dateSaved: new Date().toISOString()
        };

        saveJob(job);
        onSaveSuccess();
        alert('Job saved to tracker!');
    };

    return (
        <BackgroundLayout className="p-6">
            <div className="max-w-5xl mx-auto">
                <button
                    onClick={onBack}
                    className={`flex items-center gap-2 mb-6 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                >
                    <ArrowLeft size={20} />
                    Back
                </button>

                <div className={`rounded-xl shadow-2xl p-8 border transition-colors duration-300 ${isDarkMode
                    ? 'glass-dark border-white/10'
                    : 'bg-white border-white/50 shadow-blue-500/5'
                    }`}>
                    <div className="flex items-center gap-3 mb-6">
                        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-blue-500/20 border-blue-500/30' : 'bg-blue-50 border-blue-100'}`}>
                            <Briefcase className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} size={24} />
                        </div>
                        <div>
                            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Job Description Analyzer</h1>
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Optimize your resume for maximum interview callbacks</p>
                        </div>
                    </div>

                    {/* Job Details */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <input
                            type="text"
                            placeholder="Job Title (optional)"
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                            className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-colors ${isDarkMode
                                ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-800'
                                : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                                }`}
                        />
                        <input
                            type="text"
                            placeholder="Company (optional)"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-colors ${isDarkMode
                                ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-800'
                                : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                                }`}
                        />
                    </div>
                    <input
                        type="url"
                        placeholder="Job Link (optional)"
                        value={jobLink}
                        onChange={(e) => setJobLink(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mb-4 transition-colors ${isDarkMode
                            ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-800'
                            : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                            }`}
                    />

                    <div className="flex gap-4 mb-4">
                        <textarea
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            placeholder="Paste the job description here..."
                            className={`flex-1 h-64 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none leading-relaxed font-mono text-sm transition-colors ${isDarkMode
                                ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-800'
                                : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                                }`}
                        />
                    </div>

                    <div className="flex items-center gap-4 mb-6">
                        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${isDarkMode ? 'bg-slate-800/50 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                            <Globe size={18} className="text-slate-500" />
                            <select
                                value={selectedLanguage}
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                                className={`bg-transparent text-sm font-medium outline-none cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-800'}`}
                            >
                                {LANGUAGES.map(lang => (
                                    <option key={lang} value={lang} className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>{lang}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1"></div>
                        <p className="text-xs text-slate-500 italic">Powered by Gemini AI</p>
                    </div>

                    <button
                        onClick={handleAnalyze}
                        disabled={analyzing || !jobDescription.trim()}
                        className={`w-full font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg ${isDarkMode
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/20'
                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20'
                            }`}
                    >
                        {analyzing ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                Analyzing...
                            </>
                        ) : (
                            <>
                                <Sparkles size={20} />
                                Analyze with AI
                            </>
                        )}
                    </button>

                    {/* Analysis Results */}
                    {analysis && (
                        <div className="mt-8 space-y-6 animate-fade-in-up">
                            {/* Score Card */}
                            <div className={`p-6 rounded-xl border backdrop-blur-sm ${isDarkMode
                                ? 'bg-slate-800/40 border-white/10'
                                : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100'
                                }`}>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Match Score</h3>
                                    <div className={`text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${isDarkMode ? 'from-blue-400 to-purple-400' : 'from-blue-600 to-purple-600'}`}>{analysis.matchScore}%</div>
                                </div>
                                <div className={`w-full rounded-full h-3 overflow-hidden ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-200'}`}>
                                    <div
                                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000 ease-out"
                                        style={{ width: `${analysis.matchScore}% ` }}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className={`p-4 rounded-xl border ${isDarkMode
                                    ? 'bg-green-500/10 border-green-500/20'
                                    : 'bg-green-50 border-green-100'
                                    }`}>
                                    <h4 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-green-400' : 'text-green-800'}`}>
                                        <CheckCircle2 size={16} /> Matching Skills
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {analysis.matchingSkills && analysis.matchingSkills.map((skill, i) => (
                                            <span key={i} className={`px-2 py-1 rounded text-sm border ${isDarkMode
                                                ? 'bg-green-500/20 text-green-300 border-green-500/10'
                                                : 'bg-green-100 text-green-700 border-green-200'
                                                }`}>
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className={`p-4 rounded-xl border ${isDarkMode
                                    ? 'bg-orange-500/10 border-orange-500/20'
                                    : 'bg-orange-50 border-orange-100'
                                    }`}>
                                    <h4 className={`font-bold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-800'}`}>
                                        <BadgeAlert size={16} /> Missing Skills
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {analysis.missingSkills && analysis.missingSkills.map((skill, i) => (
                                            <span key={i} className={`px-2 py-1 rounded text-sm border ${isDarkMode
                                                ? 'bg-orange-500/20 text-orange-300 border-orange-500/10'
                                                : 'bg-orange-100 text-orange-700 border-orange-200'
                                                }`}>
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className={`p-6 rounded-xl border ${isDarkMode
                                ? 'bg-slate-800/40 border-white/10'
                                : 'bg-slate-50 border-slate-200'
                                }`}>
                                <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Suggestions to Improve</h4>
                                <ul className="space-y-3">
                                    {analysis.suggestions && analysis.suggestions.map((suggestion, i) => (
                                        <li key={i} className={`flex items-start gap-3 text-sm group ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                            <span className={`font-bold mt-1 transition-colors ${isDarkMode ? 'text-blue-400 group-hover:text-blue-300' : 'text-blue-600'}`}>•</span>
                                            <span className="leading-relaxed">{suggestion}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Cover Letter Section */}
                            <div className={`border rounded-xl p-6 mt-6 shadow-sm ${isDarkMode
                                ? 'bg-slate-800/30 border-white/10'
                                : 'bg-white border-slate-200'
                                }`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                                            <Mail className={isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} size={20} />
                                        </div>
                                        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Cover Letter</h3>
                                    </div>
                                    {!coverLetter && (
                                        <button
                                            onClick={handleGenerateCoverLetter}
                                            disabled={generatingLetter}
                                            className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-2 border ${isDarkMode
                                                ? 'bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 border-indigo-500/20'
                                                : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-transparent'
                                                }`}
                                        >
                                            {generatingLetter ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                            {hasFeature('cover_letter') ? 'Write Cover Letter' : `Unlock Cover Letter ($${getFeaturePrice('cover_letter')})`}
                                        </button>
                                    )}
                                </div>

                                {coverLetter && (
                                    <div className={`rounded-lg border p-4 animate-fade-in-up ${isDarkMode
                                        ? 'bg-slate-900/50 border-white/10'
                                        : 'bg-slate-50 border-slate-200'
                                        }`}>
                                        <div className={`mb-3 pb-3 border-b ${isDarkMode ? 'border-white/5' : 'border-slate-200'}`}>
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Subject Line</span>
                                            <p className={`font-medium mt-1 select-all ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{coverLetter.subject}</p>
                                        </div>
                                        <div>
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Content</span>
                                            <div className={`mt-2 text-sm whitespace-pre-wrap leading-relaxed select-all font-serif ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {coverLetter.content}
                                            </div>
                                        </div>
                                        <div className="mt-4 flex justify-end">
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`${coverLetter.subject} \n\n${coverLetter.content} `);
                                                    alert("Copied to clipboard!");
                                                }}
                                                className={`text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                                            >
                                                COPY ALL
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Interview Prep Section */}
                            <div className={`border rounded-xl p-6 mt-6 shadow-sm ${isDarkMode
                                ? 'bg-slate-800/30 border-white/10'
                                : 'bg-white border-slate-200'
                                }`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                                            <MessageSquare className={isDarkMode ? 'text-teal-400' : 'text-teal-600'} size={20} />
                                        </div>
                                        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>The Interviewer</h3>
                                    </div>
                                    {!interviewPrep && (
                                        <button
                                            onClick={handleGenerateInterviewPrep}
                                            disabled={generatingPrep}
                                            className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-2 border ${isDarkMode
                                                ? 'bg-teal-500/10 text-teal-300 hover:bg-teal-500/20 border-teal-500/20'
                                                : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border-transparent'
                                                }`}
                                        >
                                            {generatingPrep ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                                            Generate Questions
                                        </button>
                                    )}
                                </div>

                                {interviewPrep && (
                                    <div className="space-y-4 animate-fade-in-up">
                                        {interviewPrep.questions.map((q, i) => (
                                            <div key={i} className={`rounded-lg p-4 border ${isDarkMode
                                                ? 'bg-slate-900/50 border-white/10'
                                                : 'bg-slate-50 border-slate-200'
                                                }`}>
                                                <div className="flex items-start gap-3">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide mt-1 ${isDarkMode
                                                        ? (q.type === 'Technical' ? 'bg-blue-500/20 text-blue-300' : 'bg-orange-500/20 text-orange-300')
                                                        : (q.type === 'Technical' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700')
                                                        }`}>
                                                        {q.type}
                                                    </span>
                                                    <h4 className={`font-semibold text-lg leading-tight ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{q.question}</h4>
                                                </div>
                                                <div className={`mt-3 pl-3 border-l-2 ml-1 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Suggested Answer (STAR)</p>
                                                    <p className={`text-sm italic ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{q.suggestedAnswer}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* The Roaster Section */}
                            <div className={`border rounded-xl p-6 mt-6 shadow-sm relative overflow-hidden ${isDarkMode
                                ? 'bg-red-900/10 border-red-500/20'
                                : 'bg-red-50 border-red-200'
                                }`}>
                                {/* Fire bg effect */}
                                {isDarkMode && (
                                    <div className="absolute -right-10 -bottom-10 opacity-5 pointer-events-none">
                                        <Flame size={200} />
                                    </div>
                                )}

                                <div className="flex items-center justify-between mb-4 relative z-10">
                                    <div className="flex items-center gap-2">
                                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                                            <Flame className={isDarkMode ? 'text-red-400' : 'text-red-600'} size={20} />
                                        </div>
                                        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>The Roaster</h3>
                                    </div>
                                    {!roast && (
                                        <button
                                            onClick={handleRoastResume}
                                            disabled={roasting}
                                            className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-2 border ${isDarkMode
                                                ? 'bg-red-500/10 text-red-300 hover:bg-red-500/20 border-red-500/30'
                                                : 'bg-red-100 text-red-700 hover:bg-red-200 border-transparent'
                                                }`}
                                        >
                                            {roasting ? <Loader2 size={14} className="animate-spin" /> : <Flame size={14} />}
                                            {hasFeature('roast') ? 'Roast My Resume' : `Unlock Roast ($${getFeaturePrice('roast')})`}
                                        </button>
                                    )}
                                </div>

                                {roast && (
                                    <div className={`rounded-lg border p-6 animate-fade-in-up relative z-10 backdrop-blur-md ${isDarkMode
                                        ? 'bg-slate-900/80 border-red-500/30'
                                        : 'bg-white border-red-100'
                                        }`}>
                                        <div className={`flex items-center justify-between mb-6 border-b pb-4 ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
                                            <div>
                                                <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>Brutal Score</p>
                                                <div className="flex items-end gap-2">
                                                    <span className={`text-4xl font-black ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{roast.score}/10</span>
                                                    <span className="text-sm text-slate-400 mb-1 font-medium">Ouch.</span>
                                                </div>
                                            </div>
                                            <div className={`flex-1 h-2 ml-6 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                                <div
                                                    className={`h-full rounded-full transition-all duration-1000 ${roast.score > 7 ? 'bg-green-500' : roast.score > 4 ? 'bg-orange-500' : 'bg-red-500'} `}
                                                    style={{ width: `${roast.score * 10}% ` }}
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-6">
                                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Verdict</p>
                                            <p className={`text-lg font-medium italic ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>"{roast.oneLiner}"</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-3">
                                                <h4 className={`flex items-center gap-2 font-bold text-sm uppercase ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                                                    <BadgeAlert size={16} />
                                                    The Burns (Criticisms)
                                                </h4>
                                                <ul className="space-y-2">
                                                    {roast.criticisms.map((item, i) => (
                                                        <li key={i} className={`flex items-start gap-2 text-sm p-2 rounded border ${isDarkMode
                                                            ? 'text-slate-300 bg-red-900/20 border-red-500/10'
                                                            : 'text-slate-700 bg-red-50 border-transparent'
                                                            }`}>
                                                            <span className={`font-bold ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>•</span>
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="space-y-3">
                                                <h4 className={`flex items-center gap-2 font-bold text-sm uppercase ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                                    <CheckCircle2 size={16} />
                                                    The Fix (Suggestions)
                                                </h4>
                                                <ul className="space-y-2">
                                                    {roast.suggestedFixes.map((item, i) => (
                                                        <li key={i} className={`flex items-start gap-2 text-sm p-2 rounded border ${isDarkMode
                                                            ? 'text-slate-300 bg-green-900/20 border-green-500/10'
                                                            : 'text-slate-700 bg-green-50 border-transparent'
                                                            }`}>
                                                            <span className={`font-bold ${isDarkMode ? 'text-green-400' : 'text-green-500'}`}>•</span>
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={`p-4 rounded-xl border ${isDarkMode
                                ? 'bg-purple-500/10 border-purple-500/20'
                                : 'bg-purple-50 border-purple-200'
                                }`}>
                                <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Tailored Summary</h4>
                                <p className={`text-sm leading-relaxed font-serif ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{analysis.tailoredSummary}</p>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleSave}
                                    className={`flex-1 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg ${isDarkMode
                                        ? 'bg-green-600 hover:bg-green-700 text-white shadow-green-900/20'
                                        : 'bg-green-600 hover:bg-green-700 text-white'
                                        }`}
                                >
                                    <Save size={20} />
                                    Save to Tracker
                                </button>
                                <button
                                    onClick={() => onTailor(analysis)}
                                    className={`flex-1 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg ${isDarkMode
                                        ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-purple-900/20'
                                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                                        }`}
                                >
                                    <Sparkles size={20} />
                                    Tailor & Preview
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} />
        </BackgroundLayout>
    );
};

export default JobAnalyzer;
